import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { createChannel } from '@ego/api';
import useAuth from '../../hooks/useAuth';
import useFocusTrap from '../../hooks/useFocusTrap';
import styles from './CreateChannelModal.module.css';

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function CreateChannelModal({ onClose, ownerHandle }) {
  const { session } = useAuth();
  const dialogRef = useRef(null);
  useFocusTrap(dialogRef);
  const themeAccent = useMemo(() => getComputedStyle(document.documentElement).getPropertyValue('--accent').trim(), []);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      const slug = slugify(title);
      const channel = await createChannel({
        title: title.trim().toUpperCase(),
        slug,
        description: description.trim(),
        url: url.trim(),
        accentColor: accentColor.trim() || null,
        visibility,
        password: (visibility === 'password' || visibility === 'hidden') ? password.trim() : null,
        userId: session.user.id,
      });
      toast.success('Channel created');
      onClose();
      navigate(ownerHandle ? `/${ownerHandle}/${channel.slug}` : `/channels/${channel.slug}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog} ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="create-channel-heading">
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
        <h2 id="create-channel-heading" className={styles.heading}>New channel</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>
            <span className={styles.requiredMark}>Title</span>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className={styles.input}
              required
              autoFocus
            />
          </label>
          <label className={styles.label}>
            Description
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className={styles.textarea}
              rows={3}
            />
          </label>
          <label className={styles.label}>
            URL
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              className={styles.input}
              placeholder="https://..."
            />
          </label>
          <label className={styles.label}>
            <span className={styles.requiredMark}>Visibility</span>
            <select
              value={visibility}
              onChange={e => setVisibility(e.target.value)}
              className={styles.input}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="password">Locked</option>
              <option value="hidden">Hidden</option>
            </select>
          </label>
          {(visibility === 'password' || visibility === 'hidden') && (
            <label className={styles.label}>
              <span className={styles.requiredMark}>Password</span>
              <div className={styles.passwordInput}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={styles.input}
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  className={styles.toggleBtn}
                  onClick={() => setShowPassword(p => !p)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>
          )}
          <label className={styles.label}>
            Accent color
            <div className={styles.colorInput}>
              <input
                type="text"
                value={accentColor}
                onChange={e => setAccentColor(e.target.value)}
                className={styles.input}
                placeholder={themeAccent}
              />
              <input
                type="color"
                value={accentColor || themeAccent}
                onChange={e => setAccentColor(e.target.value)}
                className={styles.colorPicker}
              />
            </div>
          </label>
          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={styles.submitBtn} disabled={loading || ((visibility === 'password' || visibility === 'hidden') && password.trim().length < 4)}>
              {loading ? 'Creating...' : 'Create channel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
